/**
 * Similar Listings Search API Schemas
 *
 * Zod schemas for the similar listings search endpoint
 * Returns listings similar to a given listing based on title, category, and tags
 */

import { z } from 'zod';

// =============================================================================
// REQUEST SCHEMA
// =============================================================================

export const SimilarListingsQuerySchema = z.object({
  listingId: z.string().transform((val) => parseInt(val, 10)),
  limit: z.string().optional().default('12').transform((val) => parseInt(val, 10)),
  /**
   * '1' from the "no longer available" page: the listing is archived, so read
   * it from MySQL at any status, as a query only. See similar-listing-source.
   */
  gone: z.literal('1').optional(),
});

export type SimilarListingsQuery = z.infer<typeof SimilarListingsQuerySchema>;

// =============================================================================
// RESPONSE SCHEMAS
// =============================================================================

export const SimilarListingsSuccessResponseSchema = z.object({
  success: z.literal(true),
  listings: z.array(z.any()),
  total: z.number(),
});

export type SimilarListingsSuccessResponse = z.infer<typeof SimilarListingsSuccessResponseSchema>;

export const SimilarListingsErrorResponseSchema = z.object({
  success: z.literal(false),
  listings: z.array(z.any()).default([]),
  total: z.number().default(0),
  error: z.string(),
});

export type SimilarListingsErrorResponse = z.infer<typeof SimilarListingsErrorResponseSchema>;

export const SimilarListingsResponseSchema = z.discriminatedUnion('success', [
  SimilarListingsSuccessResponseSchema,
  SimilarListingsErrorResponseSchema,
]);

export type SimilarListingsResponse = z.infer<typeof SimilarListingsResponseSchema>;
